import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, App, Button, Skeleton, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined } from '@ant-design/icons';
import { getMetering } from '@/api/metering';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import type { Metering } from '@/api/types';
import { EmptyState } from '@/components/EmptyState';
import { formatAbsolute } from '@/lib/time';
import { UsageByModule } from './UsageByModule';
import { CapabilityBilling } from './CapabilityBilling';

const { Title, Paragraph, Text } = Typography;

interface SpokeMeterRow {
  spokeId: string;
  eum: number;
  agentCases: number;
}

/**
 * The backend parses `from`/`to` with Instant.parse (RFC-3339 instants) and the period is
 * half-open `[from, to)` — a native date input's bare `YYYY-MM-DD` would 400. Convert: `from` →
 * that day's start-of-day UTC; `to` → the NEXT day's start-of-day UTC, so the chosen "to" date is
 * fully included under the exclusive bound.
 */
function fromParam(date: string): string | undefined {
  return date ? `${date}T00:00:00Z` : undefined;
}
function toParam(date: string): string | undefined {
  if (!date) return undefined;
  const nextDay = new Date(Date.parse(`${date}T00:00:00Z`) + 24 * 60 * 60 * 1000);
  return nextDay.toISOString();
}

/** B1 · F-TA-09 — metering surfaces (entities-under-management + agent cases, by spoke, per period). */
export function MeteringPanel() {
  const { message } = App.useApp();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [metering, setMetering] = useState<Metering | null>(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setUnavailable(false);
    setError(null);
    try {
      setMetering(await getMetering({ from: fromParam(from), to: toParam(to) }));
    } catch (e) {
      const n = normalizeApiError(e);
      if (isNotAvailable(n)) {
        // Endpoint absent in this deployment — the teaching empty state.
        setUnavailable(true);
      } else {
        // A REAL failure (e.g. a 400 on the params) — an error banner, never the "not live yet"
        // teaching state, which would misreport a live endpoint as absent.
        setError(n.message);
        message.error(n.message);
      }
      setMetering(null);
    } finally {
      setLoading(false);
    }
    // message stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo<SpokeMeterRow[]>(() => {
    if (!metering) return [];
    const spokes = new Set<string>([
      ...Object.keys(metering.entitiesUnderManagement.bySpoke ?? {}),
      ...Object.keys(metering.agentCaseCount.bySpoke ?? {}),
    ]);
    return [...spokes]
      .map((spokeId) => ({
        spokeId,
        eum: metering.entitiesUnderManagement.bySpoke?.[spokeId] ?? 0,
        agentCases: metering.agentCaseCount.bySpoke?.[spokeId] ?? 0,
      }))
      .sort((a, b) => b.eum - a.eum);
  }, [metering]);

  const columns: ColumnsType<SpokeMeterRow> = [
    { title: 'Spoke', dataIndex: 'spokeId', key: 'spokeId', render: (v: string) => <span className="ol-mono">{v}</span> },
    {
      title: 'Entities under mgmt',
      dataIndex: 'eum',
      key: 'eum',
      align: 'right',
      width: 180,
      sorter: (a, b) => a.eum - b.eum,
      render: (v: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v.toLocaleString()}</span>,
    },
    {
      title: 'Agent cases',
      dataIndex: 'agentCases',
      key: 'agentCases',
      align: 'right',
      width: 150,
      sorter: (a, b) => a.agentCases - b.agentCases,
      render: (v: number) => <span style={{ fontVariantNumeric: 'tabular-nums' }}>{v.toLocaleString()}</span>,
    },
  ];

  if (loading) return <Skeleton active paragraph={{ rows: 5 }} />;

  if (unavailable || (!metering && !error)) {
    return (
      <EmptyState
        icon={<ReloadOutlined />}
        title="Metering"
        description="Billing-grade usage — entities under management and agent-run case counts, by spoke — lands here once the metering API is live (Track A, PS-4)."
        bullets={[
          'Entities under management (total + per spoke)',
          'Agent-run case counts (total + per spoke)',
          'The meter key and the metered period',
        ]}
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <Title level={5} style={{ margin: 0 }}>
            Metering
          </Title>
          <Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 13 }}>
            Usage this hub is metered on. Set a period to scope it, or leave blank for the current cycle.
          </Paragraph>
        </div>
        <label style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)' }}>
          <div style={{ marginBottom: 3 }}>From</div>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="ol-date-input"
            aria-label="Metering period from"
          />
        </label>
        <label style={{ fontSize: 11.5, color: 'var(--ol-text-secondary)' }}>
          <div style={{ marginBottom: 3 }}>To</div>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="ol-date-input"
            aria-label="Metering period to"
          />
        </label>
        <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>
          Apply
        </Button>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Metering request failed"
          description={error}
        />
      )}

      {metering && (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
              gap: 12,
              marginBottom: 16,
            }}
          >
            <div className="ol-card">
              <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Entities under management
              </Text>
              <div style={{ fontSize: 26, fontWeight: 700, marginTop: 2 }}>
                {metering.entitiesUnderManagement.total.toLocaleString()}
              </div>
            </div>
            <div className="ol-card">
              <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Agent cases
              </Text>
              <div style={{ fontSize: 26, fontWeight: 700, marginTop: 2 }}>
                {metering.agentCaseCount.total.toLocaleString()}
              </div>
            </div>
            <div className="ol-card">
              <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Period
              </Text>
              <div style={{ fontSize: 12.5, marginTop: 6, lineHeight: 1.5 }}>
                <div>{metering.period.from ? formatAbsolute(metering.period.from) : '—'}</div>
                <div style={{ color: 'var(--ol-text-tertiary)' }}>to {metering.period.to ? formatAbsolute(metering.period.to) : '—'}</div>
              </div>
              {metering.meterKey && (
                <Text className="ol-mono" style={{ fontSize: 10.5, color: 'var(--ol-text-tertiary)' }}>
                  key: {metering.meterKey}
                </Text>
              )}
            </div>
          </div>
        </>
      )}

      <UsageByModule />

      <CapabilityBilling />

      {metering && (
        <>
          <Table<SpokeMeterRow>
            rowKey="spokeId"
            columns={columns}
            dataSource={rows}
            size="small"
            pagination={false}
            locale={{ emptyText: 'No per-spoke usage in this period.' }}
          />
        </>
      )}
    </div>
  );
}
